import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Mail, Shield, CreditCard, FileText, Building2, Users, BarChart3, Bell, Send, Loader2, Lock, Clock, PauseCircle } from 'lucide-react';
import { getCsrfToken } from '../../utils/apiClient';
import { PushPreferencesCard } from './PushPreferencesCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const DELIVERY_HOURS = [
  { value: 9, label: '9:00 AM' },
  { value: 10, label: '10:00 AM' },
  { value: 11, label: '11:00 AM' },
  { value: 12, label: '12:00 PM' },
  { value: 13, label: '1:00 PM' },
  { value: 14, label: '2:00 PM' },
  { value: 15, label: '3:00 PM' },
  { value: 16, label: '4:00 PM' },
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMAIL_GROUPS = [
  {
    id: 'account_security',
    label: 'Account & Security',
    icon: Shield,
    description: 'Login alerts and account activity',
    items: [
      { key: 'email_welcome', label: 'Welcome email', description: 'Sent after registration', locked: false },
      
    ]
  },
  {
    id: 'billing_payments',
    label: 'Billing & Payments',
    icon: CreditCard,
    description: 'Subscription and payment notifications',
    items: [
      { key: 'email_payment_received', label: 'Payment received', description: 'When a customer pays online', locked: false },
      { key: 'email_billing_status', label: 'Billing status updates', description: 'Payment success, failure, account status', locked: false },
      { key: 'email_trial_reminder', label: 'Trial expiry reminder', description: 'Reminder before trial period ends', locked: false },
    ]
  },
  {
    id: 'invoices_bills',
    label: 'Invoices & Bills',
    icon: FileText,
    description: 'Invoice and bill notifications',
    items: [
      { key: 'email_invoice_sent', label: 'Invoice sent confirmation', description: 'When invoices are emailed to customers', locked: false },
      { key: 'email_bill_due_reminder', label: 'Bill due/overdue reminders', description: 'Daily reminder for bills approaching or past due date', locked: false },
    ]
  },
  {
    id: 'bank_integrations',
    label: 'Banking & Integrations',
    icon: Building2,
    description: 'Bank connection status updates',
    items: [
      { key: 'email_bank_connected', label: 'Bank account connected', description: 'When a bank account is successfully linked', locked: false },
      { key: 'email_bank_disconnected', label: 'Bank account disconnected', description: 'When a bank feed is disconnected', locked: false },
      { key: 'email_bank_reauth_nudge', label: 'Bank reauth reminders', description: 'Weekly nudge when your bank connection needs to be refreshed', locked: false },
      { key: 'email_weekly_bank_recap', label: 'Weekly bank-sync recap', description: 'Monday summary of last week\u2019s bank activity across all your accounts', locked: false },
      { key: 'email_monthly_recon_pack', label: 'Monthly reconciliation pack', description: 'On the 1st of each month: a reconciliation PDF for every bank account, as at the previous month-end', locked: false },
    ]
  },
  {
    id: 'team_access',
    label: 'Team & Access',
    icon: Users,
    description: 'Team member and role notifications',
    items: [
      { key: 'email_team_invitation', label: 'Team invitations', description: 'When you are invited to a business', locked: false },
      { key: 'email_role_change', label: 'Role change notifications', description: 'When your role is updated in a business', locked: false },
    ]
  },
  {
    id: 'accounting_reminders',
    label: 'Accounting Reminders',
    icon: BarChart3,
    description: 'Periodic reminders and digests',
    items: [
      { key: 'email_setup_reminders', label: 'Setup reminders', description: 'Items pending configuration in bank feed', locked: false },
      { key: 'email_month_end_alerts', label: 'Month-end alerts', description: 'Reminders as period close approaches', locked: false },
      { key: 'email_weekly_digest', label: 'Weekly digest', description: 'Weekly summary of unmatched transactions', locked: false },
      { key: 'email_budget_alerts', label: 'Budget alerts', description: 'When budget limits are approaching or exceeded', locked: false },
      { key: 'email_onboarding_series', label: 'Onboarding walkthrough', description: 'Day 0 / 3 / 7 setup nudges after registration', locked: false },
      { key: 'email_open_tasks_digest', label: 'Weekly Open Tasks digest', description: 'Friday summary of items awaiting your attention', locked: false },
      { key: 'email_activation_milestones', label: 'Milestone celebrations', description: '\ud83c\udf89 when you send your first invoice, connect a bank, close your first month', locked: false },
      { key: 'email_reengagement', label: 'Weekly check-in nudges', description: 'A gentle ping if you haven\u2019t logged in for 7 / 21 / 60 days', locked: false },
    ]
  },
  {
    id: 'product_updates',
    label: 'Product Updates',
    icon: Send,
    description: 'What\u2019s new on AkountFly',
    items: [
      { key: 'email_product_updates', label: 'Product announcements', description: 'Occasional broadcasts about new features and improvements', locked: false },
    ]
  },
  {
    id: 'critical',
    label: 'Critical Alerts',
    icon: Bell,
    description: 'These cannot be disabled for your protection',
    items: [
      { key: 'email_critical_alerts', label: 'Critical system alerts', description: 'Urgent issues requiring immediate action', locked: true },
      { key: 'email_cash_alerts', label: 'Cash flow alerts', description: 'Low balance and cash flow warnings', locked: true },
    ]
  },
];

export function NotificationPreferencesTab() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const { toast } = useToast();

  const fetchPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPrefs(data);
      }
    } catch (e) {
      console.error('Failed to fetch notification prefs:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const handleToggle = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() || '' },
        body: JSON.stringify(prefs),
      });
      if (res.ok) {
        toast({ title: 'Preferences saved', description: 'Your email notification preferences have been updated.' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to save preferences.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/send-test-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() || '' },
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Test email sent', description: 'Check your inbox for the test email.' });
      } else {
        toast({ title: 'Failed', description: data.error || 'Could not send test email.', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Error', description: 'Failed to send test email.', variant: 'destructive' });
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="notification-prefs-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notification-preferences-tab">
      {/* Master toggle */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#108981]/10">
                <Mail className="h-5 w-5 text-[#108981]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Email Notifications</p>
                <p className="text-xs text-muted-foreground">Master toggle for all non-critical email notifications</p>
              </div>
            </div>
            <Switch
              data-testid="email-master-toggle"
              checked={prefs?.email_enabled !== false}
              onCheckedChange={(v) => handleToggle('email_enabled', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Delivery window (iter1057) */}
      <Card data-testid="delivery-window-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2.5">
            <Clock className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-semibold">Delivery Time</CardTitle>
          </div>
          <CardDescription className="text-xs">
            Reminders and digests are only sent during your business hours (9 AM – 5 PM your local time).
            Pick the hour you'd like them to arrive.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0 space-y-1">
          <div className="flex items-center justify-between py-3 border-b">
            <div className="flex-1 min-w-0 pr-4">
              <Label className="text-sm font-medium">Preferred delivery hour</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Scheduled emails (bill reminders, tax deadlines, weekly summaries) arrive from this hour
              </p>
            </div>
            <Select
              value={String(prefs?.email_delivery_hour ?? 9)}
              onValueChange={(v) => handleToggle('email_delivery_hour', parseInt(v, 10))}
            >
              <SelectTrigger className="w-[120px]" data-testid="delivery-hour-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DELIVERY_HOURS.map(h => (
                  <SelectItem key={h.value} value={String(h.value)} data-testid={`delivery-hour-${h.value}`}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between py-3">
            <div className="flex-1 min-w-0 pr-4">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <PauseCircle className="h-3.5 w-3.5 text-muted-foreground" />
                Pause digests & reminders
              </Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Temporarily stop all scheduled emails. Critical alerts and instant emails (receipts, invoices you send) still deliver.
              </p>
            </div>
            <Switch
              data-testid="pause-digests-toggle"
              checked={prefs?.email_digests_paused === true}
              onCheckedChange={(v) => handleToggle('email_digests_paused', v)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Push notifications (iter1027) */}
      <PushPreferencesCard />

      {/* Grouped preferences */}
      {EMAIL_GROUPS.map(group => {
        const Icon = group.icon;
        return (
          <Card key={group.id} className={prefs?.email_enabled === false && group.id !== 'critical' ? 'opacity-50 pointer-events-none' : ''}>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2.5">
                <Icon className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-sm font-semibold">{group.label}</CardTitle>
              </div>
              <CardDescription className="text-xs">{group.description}</CardDescription>
            </CardHeader>
            <CardContent className="pt-0 space-y-1">
              {group.items.map(item => (
                <div
                  key={item.key}
                  className="flex items-center justify-between py-3 border-b last:border-0"
                  data-testid={`email-pref-${item.key}`}
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <Label className="text-sm font-medium flex items-center gap-1.5">
                      {item.label}
                      {item.locked && <Lock className="h-3 w-3 text-muted-foreground" />}
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  </div>
                  <Switch
                    data-testid={`toggle-${item.key}`}
                    checked={prefs?.[item.key] !== false}
                    onCheckedChange={(v) => handleToggle(item.key, v)}
                    disabled={item.locked}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          data-testid="save-email-prefs-btn"
          onClick={handleSave}
          disabled={saving}
          className="bg-[#108981] hover:bg-[#0d7a73]"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Save Preferences
        </Button>
        <Button
          data-testid="send-test-email-btn"
          variant="outline"
          onClick={handleSendTest}
          disabled={sendingTest}
        >
          {sendingTest ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
          Send Test Email
        </Button>
      </div>
    </div>
  );
}
